import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageContainer from './components/ui/PageContainer';
import Button, { buttonClasses } from './components/ui/Button';
import EventCard from './components/EventCard';
import { useAuth } from './auth/AuthProvider';
import { useFavourites } from './hooks/useFavourites';
import { usePageMeta } from './seo/usePageMeta';
import { SPORTS, browseEvents, fetchSavedPostcode } from './lib/events';
import { validatePostcode } from './validation/validatePostcode';
import { EventDetails } from './types';

const inputClass =
  'w-full rounded-xl border border-surface-subtle bg-white px-4 py-3 text-sm text-ink shadow-sm focus:border-brand-400 focus:outline-none focus:ring-4 focus:ring-brand-100';

const CardGrid: React.FC<{
  events: EventDetails[];
  loading: boolean;
  favouriteIds: Set<number>;
  onToggle: (e: EventDetails) => void;
}> = ({ events, loading, favouriteIds, onToggle }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {loading
      ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-surface-subtle" />)
      : events.map((e) => <EventCard key={e.Id} event={e} favourited={favouriteIds.has(e.Id)} onToggleFavourite={onToggle} />)}
  </div>
);

const HomePage: React.FC = () => {
  usePageMeta({ canonicalPath: '/' });
  const navigate = useNavigate();
  const { session } = useAuth();
  const { favouriteIds, toggleFavourite } = useFavourites();

  const [query, setQuery] = useState('');
  const [comingUp, setComingUp] = useState<EventDetails[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingComingUp, setLoadingComingUp] = useState(true);

  const [postcode, setPostcode] = useState('');
  const [postcodeError, setPostcodeError] = useState('');
  const [nearby, setNearby] = useState<EventDetails[] | null>(null);
  const [nearbyPostcode, setNearbyPostcode] = useState('');
  const [loadingNearby, setLoadingNearby] = useState(false);

  useEffect(() => {
    browseEvents({}, 6)
      .then((r) => {
        setComingUp(r.events);
        setTotal(r.total);
      })
      .catch((err) => console.error('Error loading upcoming events:', err))
      .finally(() => setLoadingComingUp(false));
  }, []);

  // The same search as /events?near=..., so "See all" shows the full list.
  const loadNearby = async (pc: string) => {
    setLoadingNearby(true);
    try {
      const r = await browseEvents({ near: pc }, 6);
      setNearby(r.events);
      setNearbyPostcode(pc);
    } catch (err) {
      console.error('Error loading nearby events:', err);
      setNearby(null);
      setPostcodeError(`We couldn't find the postcode ${pc}.`);
    } finally {
      setLoadingNearby(false);
    }
  };

  // Signed-in users get events near their saved postcode straight away.
  useEffect(() => {
    if (!session) return;
    fetchSavedPostcode()
      .then((pc) => {
        if (!pc) return;
        setPostcode(pc);
        loadNearby(pc);
      })
      .catch((err) => console.error('Error loading your postcode:', err));
  }, [session]);

  const handlePostcode = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validatePostcode(postcode);
    setPostcodeError(err);
    if (!err) loadNearby(postcode.trim().toUpperCase());
  };

  return (
    <>
      <section className="bg-gradient-to-b from-brand-50 to-surface-muted">
        <PageContainer className="py-12 sm:py-16">
          <h1 className="max-w-2xl text-3xl font-bold leading-tight text-ink sm:text-4xl">
            Find your next triathlon, run, swim or ride
          </h1>
          <p className="mt-3 max-w-xl text-base text-ink-muted">
            {total !== null ? `${total} upcoming events` : 'Upcoming events'} across Australia, with course maps, distances
            and a free training plan for the races you pick.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              navigate(query.trim() ? `/events?q=${encodeURIComponent(query.trim())}` : '/events');
            }}
            className="mt-6 flex max-w-xl gap-2"
          >
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by event, town or organiser"
              aria-label="Search events"
              className={inputClass}
            />
            <Button type="submit">Search</Button>
          </form>
          <div className="mt-4 flex flex-wrap gap-2">
            {SPORTS.map((s) => (
              <Link
                key={s}
                to={`/events?sport=${encodeURIComponent(s)}`}
                className="rounded-full border border-surface-subtle bg-white px-3 py-1.5 text-sm font-medium text-ink shadow-sm hover:border-brand-300 hover:text-brand-700"
              >
                {s}
              </Link>
            ))}
          </div>
        </PageContainer>
      </section>

      <PageContainer className="flex flex-col gap-12 py-10">
        <section>
          <div className="mb-4 flex items-end justify-between">
            <h2 className="text-xl font-bold text-ink">Coming up next</h2>
            <Link to="/events" className="text-sm font-medium text-brand-600 hover:text-brand-700">
              See all events →
            </Link>
          </div>
          <CardGrid events={comingUp} loading={loadingComingUp} favouriteIds={favouriteIds} onToggle={toggleFavourite} />
        </section>

        <section>
          <div className="mb-4 flex items-end justify-between">
            <h2 className="text-xl font-bold text-ink">Recommended near you</h2>
            {nearbyPostcode && nearby && nearby.length > 0 && (
              <Link
                to={`/events?near=${encodeURIComponent(nearbyPostcode)}`}
                className="text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                See all near {nearbyPostcode} →
              </Link>
            )}
          </div>
          <form onSubmit={handlePostcode} className="mb-4 flex max-w-md flex-col gap-2 sm:flex-row sm:items-start">
            <div className="flex-1">
              <input
                type="text"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                placeholder="Your postcode, e.g. 2000"
                aria-label="Postcode"
                className={inputClass}
              />
              {postcodeError && <p className="mt-1 text-xs font-medium text-red-600">{postcodeError}</p>}
            </div>
            <Button type="submit">Show events</Button>
          </form>
          {nearby === null && !loadingNearby ? (
            <p className="text-sm text-ink-muted">
              Enter a postcode to see events within 100 km.
              {session && ' Save your postcode on your account page to see these automatically.'}
            </p>
          ) : nearby && nearby.length === 0 && !loadingNearby ? (
            <p className="text-sm text-ink-muted">
              No upcoming events within 100 km of {nearbyPostcode} yet.{' '}
              <Link
                to={`/events?near=${encodeURIComponent(nearbyPostcode)}&within=250`}
                className="font-medium text-brand-600 hover:text-brand-700"
              >
                Look further afield
              </Link>
            </p>
          ) : (
            <CardGrid events={nearby ?? []} loading={loadingNearby} favouriteIds={favouriteIds} onToggle={toggleFavourite} />
          )}
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-surface-subtle bg-white p-6 shadow-card">
            <h2 className="text-lg font-bold text-ink">Plan your season</h2>
            <p className="mt-2 text-sm text-ink-muted">
              Save the races you&apos;re doing and get a free week-by-week training plan built around them.
            </p>
            <Link to={session ? '/event-planner' : '/create-user'} className={buttonClasses('primary', false, 'mt-4')}>
              {session ? 'Open your event planner' : 'Create a free account'}
            </Link>
          </div>
          <div className="rounded-2xl border border-surface-subtle bg-white p-6 shadow-card">
            <h2 className="text-lg font-bold text-ink">Organising an event?</h2>
            <p className="mt-2 text-sm text-ink-muted">
              Tell us about it and we&apos;ll add it to Sport Sphere, free.
            </p>
            <Link to="/submit-event" className={buttonClasses('secondary', false, 'mt-4')}>
              Submit an event
            </Link>
          </div>
        </section>
      </PageContainer>
    </>
  );
};

export default HomePage;
